
// Boards v2 lives in its own Vue 3 + PixiJS package (web/board). This page is
// only the seam: it loads that bundle lazily, mounts it into a <body>-level
// portal (outside .v-application, so Vuetify's scoped styles can't leak in),
// hands it this app's Firebase handles and signed-in user, and tears it down
// on leave. It mounts only for an account that has Boards v2, and sends one
// without it to the classic boards list.
import Vue from 'vue';
import { mapGetters, mapState } from 'vuex';
import { db, auth, functions, functionsBaseUrl, storage, FieldValue, FieldPath, storageFileUrl } from '@/firebase';
import { ROUTER } from '@/router/constants';
import { uploadAsset3DToRunnit, uploadImageRecordForUseCase, uploadImageToRunnit, uploadVideoToRunnit } from '@/utils/runnitUpload';
import { getRunnitUploadFilePath, RunnitMask, RUNNIT_MASK_USE_CASE } from '@run-diffusion/shared';
import { getUploadImageErrorMessage } from '@/utils/uploadErrors';
import { fetchLibraryPage, LibraryFetchResult, LibraryRequest } from '@/utils/libraryApi';
import { ensureWebClientDeviceId } from '@/utils/webClientDevice';
import { forgetWebClientDeviceIfRejected, internalApiCredentials } from '@/services/internalApiCredentials';
import { TeamUserMixin } from '@/mixins/TeamUserMixin';
import { BoardsV2AccessMixin } from '@/mixins/BoardsV2AccessMixin';
import type { BoardsV2Access } from '@/utils/boardsVersion';
import { analytics, EVENTS } from '@/services/analytics';
import { BOARDS_DEV_BACKEND } from '@/utils/boardsDevBackend';
import { loadBoardBabylon } from './boardBabylon';
import type { MountedBoard } from '@board-app';
import MarkLoader from '@/components/base/MarkLoader.vue';

// The board's events, as the events v1's boards track (the board sends v1's
// property names, and board_type CANVAS to tell them apart).
const BOARD_EVENTS: Record<string, string> = {
	generation_run: EVENTS.BOARD_GENERATION_RUN,
	image_downloaded: EVENTS.IMAGE_DOWNLOADED,
	remix_initiated: EVENTS.REMIX_INITIATED,
	result_favorited: EVENTS.RESULT_FAVORITED,
};

// Images dropped or pasted onto a board go through the same pipeline as the
// tools' uploads: normalised server-side (HEIC to JPEG), deduplicated by hash
// and recorded in the user's uploads library. The record comes back with the
// URL: a run that uses the image as an input sends it. A failure carries the
// same user-facing copy the tools show; the board puts it in its toast.
async function uploadToLibrary (file: File): Promise<{ url: string, upload: Record<string, unknown> }> {
	try {
		const upload = await uploadImageToRunnit(file);
		const { filePath } = getRunnitUploadFilePath(upload);
		return { url: storageFileUrl(filePath), upload: { ...upload } };
	} catch (e) {
		const err: Error & { userMessage?: string, cause?: unknown } = new Error(`Upload of ${file.name} failed`);
		err.userMessage = getUploadImageErrorMessage(e);
		err.cause = e;
		throw err;
	}
}

// A 3D model brought into the board's Studios view goes where v1's Use 3D
// keeps one: your uploads, as an ASSET_3D upload (deduplicated by hash, its
// preview rendered here), so it's in your Library next time. The board loads
// the file from its URL.
async function upload3dToLibrary (file: File): Promise<{ url: string, upload: Record<string, unknown> }> {
	const current = auth.currentUser;
	if (!current) throw new Error('Sign in to keep a model.');
	try {
		const upload = await uploadAsset3DToRunnit(file, current.uid);
		const { filePath } = getRunnitUploadFilePath(upload);
		return { url: storageFileUrl(filePath), upload: { ...upload, id: upload.id } };
	} catch (e) {
		const err: Error & { userMessage?: string, cause?: unknown } = new Error(`Upload of ${file.name} failed`);
		err.userMessage = e instanceof Error && /Unsupported 3D asset format/.test(e.message)
			? `${file.name} isn't a 3D model the board reads. Use .glb, .gltf, .obj or .stl.`
			: `${file.name} couldn't be kept in your Library. Please try again.`;
		err.cause = e;
		throw err;
	}
}

// A video brought into a Video Edit session's Studios view (TD-13) goes where
// v1 keeps one: your uploads, as a VID upload (deduplicated by hash), so it's
// in your Library next time. The run sends its record as the video input.
async function uploadVideoToLibrary (file: File): Promise<{ url: string, upload: Record<string, unknown> }> {
	const current = auth.currentUser;
	if (!current) throw new Error('Sign in to keep a video.');
	try {
		const upload = await uploadVideoToRunnit(file, current.uid);
		const { filePath } = getRunnitUploadFilePath(upload);
		return { url: storageFileUrl(filePath), upload: { ...upload, id: upload.id } };
	} catch (e) {
		const err: Error & { userMessage?: string, cause?: unknown } = new Error(`Upload of ${file.name} failed`);
		err.userMessage = 'That video couldn\'t be uploaded. Try an MP4 or MOV file.';
		err.cause = e;
		throw err;
	}
}

// An area chosen on the board's Edit side (Erase, or an edit kept to part of a
// picture) is sent as a mask, kept where v1's inpainting editor keeps one: a
// runnitMasks record whose file is the mask byte for byte, so it stays the
// picture's size. A run sends the record with its picture.
async function uploadMaskRecord (file: File): Promise<Record<string, unknown>> {
	try {
		const mask: RunnitMask = await uploadImageRecordForUseCase<RunnitMask>({
			file,
			uploadType: 'RUNNIT_MASK',
			useCase: RUNNIT_MASK_USE_CASE.INPAINTING,
		});
		return { ...mask, id: mask.id };
	} catch (e) {
		const err: Error & { cause?: unknown } = new Error('The area couldn\'t be saved for this edit. Please try again.');
		err.cause = e;
		throw err;
	}
}

// The board's Library reads its filters, its search (admins, until it's
// released) and its Favorites from the Library API, exactly as v1's Library
// dialog does: this browser's sign-in, and its device registration (which is
// what makes the API serve the run shape the board maps), ride along.
const LIBRARY_API_BASE_URL: string = process.env.VUE_APP_API_BASE_URL || 'https://api2.rundiffusion.com/api/v2';

async function libraryPage (request: LibraryRequest): Promise<LibraryFetchResult> {
	const current = auth.currentUser;
	if (!current) return { ok: false, reason: 'unauthorized' };
	let idToken: string;
	try {
		idToken = await current.getIdToken();
	} catch (e) {
		console.error('[board] Library: failed to acquire id token', e);
		return { ok: false, reason: 'unauthorized' };
	}
	const deviceId = await ensureWebClientDeviceId({ baseUrl: LIBRARY_API_BASE_URL, idToken });
	return fetchLibraryPage({ baseUrl: LIBRARY_API_BASE_URL, idToken, deviceId, request });
}

// Development only: `npm run dev` rebuilds the board bundle as you edit it.
// Swap the new bundle into the open page in place (same board, same view)
// rather than leaving the old one mounted until a reload.
let hotRemount: (() => void) | null = null;
if (process.env.NODE_ENV !== 'production' && module.hot) {
	module.hot.accept('@board-app', () => {
		if (hotRemount) hotRemount();
	});
}

export default Vue.extend({
	name: 'BoardPage',
	components: {
		MarkLoader,
	},
	// team-level tool blocks, applied to the board's kits as everywhere else
	mixins: [TeamUserMixin, BoardsV2AccessMixin],
	props: {
		// /board/stress: the stress test board (in memory, never saved)
		stress: { type: Boolean, default: false },
	},
	// the board's own name once it has loaded: vue-meta sets the title again
	// on every route change (the camera writes the address as you pan), so the
	// board tells this page its name rather than setting document.title itself
	metaInfo () {
		return {
			title: `${this.boardTitle || 'Board'} | RunDiffusion`,
		};
	},
	data () {
		return {
			boardTitle: null as string | null,
			loadError: null as string | null,
			board: null as MountedBoard | null,
			mountStarted: false,
			portal: null as HTMLElement | null,
			prevHtmlOverflow: '',
			prevBodyOverscroll: '',
			destroyed: false,
		};
	},
	computed: {
		...mapState(['user', 'authUser', 'team']),
		// the company's renamed concepts (Team), for the board's copy
		...mapGetters(['displayLabels']),
		boardId (): string | null {
			return (this.$route.params.boardId as string) || null;
		},
	},
	watch: {
		boardsV2Access () {
			this.applyAccess();
		},
		boardId (id: string | null) {
			if (this.board && !this.stress) this.board.setBoardId(id);
		},
		// /board/stress and /board share this page, so the router keeps it
		// mounted between them: swap which board app runs
		stress () {
			this.reload();
		},
		// the role (and its tool blocks) loads after the page, and can change
		teamUserRole () {
			if (this.board) this.board.refreshTools();
		},
		// who may buy a team's tokens (MANAGE_COMPANY) is known once the company role loads (N5)
		hasManageCompanyPermission () {
			if (this.board) this.board.refreshTokenAccess();
		},
	},
	mounted () {
		const html: HTMLElement = document.documentElement;
		this.prevHtmlOverflow = html.style.overflow;
		this.prevBodyOverscroll = document.body.style.overscrollBehavior;
		html.style.overflow = 'hidden';
		document.body.style.overscrollBehavior = 'none';
		hotRemount = () => this.reload();
		this.applyAccess();
	},
	beforeDestroy () {
		this.destroyed = true;
		hotRemount = null;
		this.teardown();
		document.documentElement.style.overflow = this.prevHtmlOverflow;
		document.body.style.overscrollBehavior = this.prevBodyOverscroll;
	},
	methods: {
		// Waits while the account loads; mounts the board once it has Boards v2.
		// The stress board (in memory, never saved) is a developer's page: its
		// route decides who may open it.
		applyAccess () {
			if (this.destroyed) return;
			const access: BoardsV2Access = this.stress ? 'on' : this.boardsV2Access;
			if (access === 'off') {
				this.leaveBoardsV2();
				return;
			}
			if (access === 'on' && !this.mountStarted) this.mount();
		},
		async mount () {
			this.mountStarted = true;
			this.loadError = null;
			try {
				const mod = await import(/* webpackChunkName: "board-app" */ '@board-app');
				if (this.destroyed) return;
				const portal: HTMLElement = document.createElement('div');
				portal.className = 'board-page-portal';
				document.body.appendChild(portal);
				this.portal = portal;
				const user = {
					uid: this.authUser ? this.authUser.uid : (this.user && this.user.id),
					name: (this.user && (this.user.name || this.user.displayName)) || (this.authUser && this.authUser.displayName) || '',
					email: (this.user && this.user.email) || (this.authUser && this.authUser.email) || '',
					isAdmin: !!(this.user && this.user.isAdmin),
					teamId: this.team ? this.team.id : null,
					// v1's freemium passport (calcRunnitTokensDataForBalanceAccount): a paid plan, or a passport
					freemiumPassport: !!(this.user && (
						(this.user.clubOffer && !this.user.clubOffer.isFreemium) ||
						(this.user.freemiumInfo && this.user.freemiumInfo.runnitFreemiumPassportId)
					)),
				};
				if (this.stress) {
					this.board = mod.mountStressBoard(portal, {
						images: Number(this.$route.query.images) || undefined,
						user,
						onImages: (images: number) => {
							this.$router.replace({ query: { ...this.$route.query, images: String(images) } }).catch(() => {});
						},
						onIndex: () => {
							this.$router.push({ name: ROUTER.RUNNIT_BOARDS_V2_PAGE }).catch(() => {});
						},
						navigate: (path: string) => {
							if (this.$route.fullPath !== path) this.$router.push(path).catch(() => {});
						},
					});
					return;
				}
				this.board = mod.mountBoard(portal, {
					boardId: this.boardId,
					user,
					firebase: {
						db,
						auth,
						functions,
						storage,
						FieldValue,
						FieldPath,
						upload: uploadToLibrary,
						upload3d: upload3dToLibrary,
						uploadVideo: uploadVideoToLibrary,
						uploadMask: uploadMaskRecord,
						storageUrl: storageFileUrl,
						library: libraryPage,
						// the prompt library's My prompts (GET /api/v2/internal/library-prompts)
						api: {
							baseUrl: LIBRARY_API_BASE_URL,
							credentials: internalApiCredentials,
							onRejected: forgetWebClientDeviceIfRejected,
						},
					},
					// the Studios view's 3D, on this app's Babylon (loaded when 3D first opens)
					babylon: loadBoardBabylon,
					// the board's developer tools (local builds only) say which data this build reads
					backend: BOARDS_DEV_BACKEND,
					navigate: (path: string) => {
						if (this.$route.fullPath !== path) this.$router.push(path).catch(() => {});
					},
					// a key given undefined leaves the address (a comment link's thread, once it's shown)
					replaceQuery: (query: Record<string, string | undefined>) => {
						const next: Record<string, string | (string | null)[]> = { ...this.$route.query };
						for (const [key, value] of Object.entries(query)) {
							if (value === undefined) delete next[key];
							else next[key] = value;
						}
						this.$router.replace({ query: next }).catch(() => {});
					},
					onTitle: (title: string) => {
						this.boardTitle = title;
					},
					openBoard: (id: string | null) => {
						const route = id
							? { name: ROUTER.BOARD, params: { boardId: id } }
							// no board: back to the boards list
							: { name: ROUTER.RUNNIT_BOARDS_V2_PAGE };
						this.$router.push(route).catch(() => {});
					},
					initialQuery: { ...this.$route.query },
					// where v1 sends you for more tokens: a team's plan, or your subscription
					getTokens: (teamId: string | null) => {
						const route = teamId
							? { name: ROUTER.TEAM_DASHBOARD, params: { teamId } }
							: { name: ROUTER.RUNNITS_SUBSCRIPTIONS };
						this.$router.push(route).catch(() => {});
					},
					// only someone who manages the company buys its team's tokens; the rest are pointed at an admin (N5)
					canBuyTokens: (teamId: string) => !!(this.user && this.user.isAdmin) || (!!this.team && this.team.id === teamId && this.hasManageCompanyPermission),
					track: (event: string, props: Record<string, string | number | boolean | null>) => {
						const name = BOARD_EVENTS[event];
						if (name) analytics.track(name, props);
					},
					isToolBlocked: (nodeDef: Record<string, unknown>) => this.isNodeDefBlockedForTeamUser(nodeDef as any),
					labels: { team: this.displayLabels.team },
					// the board menu's Take the Tour (IL-095): back to this board after
					openTour: (boardId: string) => {
						this.$router.push({ name: ROUTER.BOARDS_TOUR, query: { reason: 'replay', board: boardId } }).catch(() => {});
					},
					// Smart Frames' chat: a deployed build asks the smartFramesAgent function as the signed-in
					// person; a local build (npm run dev) asks the dev server's own /__smart-frames instead
					agent: process.env.NODE_ENV === 'production'
						? {
							url: functionsBaseUrl + '/smartFramesAgent',
							token: () => (auth.currentUser ? auth.currentUser.getIdToken() : Promise.resolve(null)),
						}
						: undefined,
				});
			} catch (e) {
				console.error('[board] failed to mount', e);
				this.loadError = (e && (e as Error).message) || 'Unknown error';
			}
		},
		teardown () {
			this.mountStarted = false;
			if (this.board) {
				try {
					this.board.unmount();
				} catch (e) {
					console.error('[board] failed to unmount cleanly', e);
				}
				this.board = null;
			}
			if (this.portal && this.portal.parentNode) this.portal.parentNode.removeChild(this.portal);
			this.portal = null;
		},
		reload () {
			this.teardown();
			this.applyAccess();
		},
	},
});
